import _ from 'lodash';
import moment, {Duration, Moment} from 'moment';
import {getSisDateAsISO, getSisDurationAsISO, MomFmt, parseSisDate, parseSisDateTime, parseSisDuration} from 'util/SisDateTime';
import {SisName} from 'util/SisName';
import {PersonAttr} from 'module/selpan/impl/person/model/PersonUtil';
import {BSModelIDPart, BSModelIDType, BSModelType} from 'module/BSCollection/type/BSCollectionStateType';
import {Booking, EntryDesc, Holiday, IstZeit, KoKeyConf, Persurl, PlanEvent, StatusForecast, StTagZu, TaskZu, V2, V2Data, VlFehl} from 'generated/JavaTOmodifications';
import {CFModel} from 'module/CustomField/type/CFType';

/**
 * @typedef StTagZu
 * @property feierKey
 * @property feierDatum
 * @property text
 * @property compositeId
 */

export const BSAttr = {
	getId: function (model?: BSModelIDPart | CFModel | Booking | Persurl | null): BSModelIDType | null {
		switch (model?.sisName) {
			case SisName.Booking:
				return (model as Booking).bookingNr
			case SisName.Persurl:
				return (model as Persurl).id
			case SisName.V2:
				return (model as V2).rowId
			case SisName.V2Data:
				return (model as V2Data).id
			case SisName.Ist:
				return (model as IstZeit).compositeId
			case SisName.StTagZu:
				return (model as StTagZu).compositeId
			case SisName.Holiday:
				return (model as Holiday).compositeId
			case SisName.TaskZu:
				return (model as TaskZu).id
			case SisName.StatusForecast:
				return (model as StatusForecast).compositeId
			case SisName.PlanEvent:
				return (model as PlanEvent).id
			case SisName.VlFehl:
				let vle = model as VlFehl;
				return vle.persNr + '_' + vle.datum
			case SisName.KoKeyConf:
				return (model as KoKeyConf).persNr
		}
		return null
	},
	/**
	 * @return {SisDateTime}
	 */
	getStartDateTime: function (model?: BSModelType): Moment {
		switch (model?.sisName) {
			case SisName.PlanEvent:
			case SisName.Booking:
				return parseSisDateTime(model.bookingDate, model.bookingTime, {ignoreSeconds: true})
			case SisName.Persurl:
				return parseSisDate(model.start)
			case SisName.V2:
				return parseSisDateTime(model.datumVon, model.zeitVon)
			case SisName.V2Data:
				return parseSisDateTime(model.fromDate, model.fromTime)
			case SisName.Ist:
			case SisName.StTagZu:
				return parseSisDate(model.date)
			case SisName.Holiday:
				return parseSisDate(model.feierDatum)
			case SisName.TaskZu:
			case SisName.StatusForecast:
				return parseSisDateTime(model.vondat || 19700101, model.vonzeit)
		}
		return moment.invalid()
	},
	getEndDateTime: function (model?: BSModelType): Moment {
		switch (model?.sisName) {
			case SisName.V2:
			case SisName.V2Data:
			case SisName.PlanEvent:
			case SisName.Booking://todo timespantype! may be just an amount
				return BSAttr.getStartDateTime(model).add(BSAttr.getDuration(model))
			case SisName.Persurl:
				return parseSisDate(model.end).endOf('day')
			case SisName.StTagZu:
			case SisName.Holiday:
			case SisName.Ist:
				return BSAttr.getStartDateTime(model).endOf('day')
			case SisName.TaskZu:
			case SisName.StatusForecast:
				return parseSisDateTime(model.bisdat || 99991231, model.biszeit);
		}
		return moment.invalid()
	},
	getDuration: function (model?: BSModelType): Duration {
		switch (model?.sisName) {
			case SisName.PlanEvent:
			case SisName.Booking:
				//todo timespantype! may be just an amount
				return parseSisDuration(model.bookingTimespan);
			case SisName.Persurl:
				return moment.duration(BSAttr.getEndDateTime(model).add(1, 'd').diff(BSAttr.getStartDateTime(model)));
			case SisName.V2:
				return parseSisDuration(model.zsp);
			case SisName.V2Data:
				return parseSisDuration(model.bookingTimespan)
			case SisName.Ist:
			case SisName.Holiday:
			case SisName.StTagZu:
				return moment.duration(1, 'day');
			case SisName.TaskZu:
			case SisName.StatusForecast:
				return moment.duration((+BSAttr.getEndDateTime(model)) - (+BSAttr.getStartDateTime(model)));
		}
		return moment.duration(NaN)
	},
	toString: function (model?: BSModelType, entryDesc?: EntryDesc, person = {}) {
		if (!model) return;
		let ret: any[];
		switch (model.sisName) {
			case SisName.Booking: {
				const edStr = entryDesc && entryDesc.longLabel;
				const startStr = BSAttr.getStartDateTime(model).format(MomFmt.DATETIME_OUT);
				//todo duplicate impl in CalendarBookingModel.js
				ret = [
					PersonAttr.getStringRepresentation(person),
					edStr,
					startStr,
					model.bookingTimespan && "Zeitspanne " + getSisDurationAsISO(model.bookingTimespan),
					model.text?.trim() && model.text,
				];
				break;
			}
			case SisName.Persurl: {
				const edStr = entryDesc && entryDesc.longLabel;
				const startStr = BSAttr.getStartDateTime(model).format(MomFmt.DATEISO);
				const endStr = BSAttr.getEndDateTime(model).format(MomFmt.DATEISO);
				ret = [
					PersonAttr.getStringRepresentation(person),
					edStr,
					startStr + ' bis ' + endStr,
					model.text?.trim() && model.text,
				];
				break;
			}
			case SisName.V2: {
				const startStr = BSAttr.getStartDateTime(model).format(MomFmt.DATETIME_OUT);
				ret = [
					PersonAttr.getStringRepresentation(person),
					model.stuarttext,
					startStr,
					getSisDurationAsISO(model.zsp),
				];
				break;
			}
			case SisName.Ist: {
				ret = [
					PersonAttr.getStringRepresentation(person),
					getSisDateAsISO(model.date),
					`Ist-Zeit: ${getSisDurationAsISO(model.ist)}`,
				];
				break;
			}
			case SisName.StTagZu: {
				ret = [
					PersonAttr.getStringRepresentation(person),
					getSisDateAsISO(model.date),
					model.stTagKey,
				];
				break;
			}
			case SisName.Holiday: {
				ret = [
					model.text && model.text.trim(),
					"Feiertag am " + getSisDateAsISO(model.feierDatum),
				];
				break;
			}
			default:
				ret = [];
				break;
		}
		return _.compact(ret).join('\n');
	},
};